// SM-2 spaced repetition scheduler for Empirio Learn review prompts.
// Intervals in days: first review next day, then weekly, monthly, etc.
const INTERVALS_DAYS = [1, 6, 12, 30, 90, 180];
const STORAGE_KEY = 'lumio_sm2_schedule';

interface ReviewRecord {
  conceptId: string;
  intervalIndex: number; // index into INTERVALS_DAYS
  nextReview: number;    // Unix timestamp ms
}

function loadAll(): Record<string, ReviewRecord> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveAll(data: Record<string, ReviewRecord>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

/** Returns true if this concept is due for review right now. */
export function isDue(conceptId: string): boolean {
  const all = loadAll();
  const rec = all[conceptId];
  if (!rec) return false;
  return Date.now() >= rec.nextReview;
}

/** Returns all concept IDs that are currently due. */
export function getDueIds(): string[] {
  const all = loadAll();
  const now = Date.now();
  return Object.values(all)
    .filter(r => now >= r.nextReview)
    .map(r => r.conceptId);
}

/** Call when a lesson is completed to schedule its first review. */
export function scheduleReview(conceptId: string): void {
  const all = loadAll();
  if (all[conceptId]) return; // already scheduled
  all[conceptId] = {
    conceptId,
    intervalIndex: 0,
    nextReview: Date.now() + INTERVALS_DAYS[0] * 864e5,
  };
  saveAll(all);
}

/** Call when a review prompt is answered. Advances or resets the interval. */
export function recordResult(conceptId: string, correct: boolean): void {
  const all = loadAll();
  const rec = all[conceptId];
  if (!rec) return;
  if (correct) {
    rec.intervalIndex = Math.min(rec.intervalIndex + 1, INTERVALS_DAYS.length - 1);
  } else {
    rec.intervalIndex = 0;
  }
  rec.nextReview = Date.now() + INTERVALS_DAYS[rec.intervalIndex] * 864e5;
  all[conceptId] = rec;
  saveAll(all);
}
